import { useState } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { withSessionRedirect } from '../lib/require-session.ts'
import {
  Button,
  Callout,
  Code,
  EmptyState,
  PageHeader,
  Toolbar,
} from '../components/ui/index.ts'
import { VaultItemEditor } from '../components/VaultItemEditor.tsx'
import { VaultItemsTable } from '../components/VaultItemsTable.tsx'
import { VaultNodeTree } from '../components/VaultNodeTree.tsx'
import { fetchHierarchy } from '../lib/hierarchy-api.ts'
import { countDescendants, nodePathOf } from '../lib/cross-links.ts'
import { OpenInProjectsLink } from '../components/OpenInProjectsLink.tsx'
import { findInTree } from '../lib/entity-selection.ts'
import {
  deleteItem,
  getTenantKey,
  listEnvironments,
  listItems,
} from '../lib/vault-api.ts'
import type { VaultItemRow } from '../lib/vault-api.ts'

export interface InheritedItemRow {
  item: VaultItemRow
  sourcePath: string
}

export const Route = createFileRoute('/vault/projects')({
  validateSearch: (search: Record<string, unknown>): { node?: string } => ({
    node: typeof search.node === 'string' ? search.node : undefined,
  }),
  loaderDeps: ({ search }) => ({ node: search.node }),
  loader: async ({ deps }) =>
    withSessionRedirect(async () => {
      const tree = await fetchHierarchy()
      const rootId = tree.roots[0]?.id ?? ''
      if (!rootId)
        return {
          tree,
          rootId,
          environments: [],
          tenantPubkey: null,
          currentKekVersion: null,
          nodeId: null,
          nodeKind: null,
          nodePath: '',
          nodeDescendants: 0,
          items: [],
          inherited: [],
        }

      const [environments, tenantKey] = await Promise.all([
        listEnvironments(rootId),
        getTenantKey(rootId),
      ])

      const nodeId = deps.node ?? tree.roots[0]?.id ?? null
      // `findInTree`'s `kind` param only steers workspace-vs-node lookup —
      // passing 'entity' still matches a server node by id (see the note in
      // `entity-selection.ts`), so this works for either kind.
      const found = nodeId ? findInTree(tree, 'entity', nodeId) : null
      // Same joiner as the tree's hover text and the Projects page's
      // "Open vault" line — see `NODE_PATH_SEPARATOR`.
      const nodePath = found ? nodePathOf(found.ancestors) : ''
      const ancestorIds = found
        ? found.ancestors.slice(0, -1).map((a) => a.id)
        : []
      const ancestorPaths = found
        ? found.ancestors.slice(0, -1).map((_, i) =>
            found.ancestors
              .slice(0, i + 1)
              .map((a) => a.name)
              .join(' / '),
          )
        : []

      const [items, inheritedLists] = await Promise.all([
        nodeId ? listItems(rootId, nodeId) : Promise.resolve([]),
        Promise.all(ancestorIds.map((id) => listItems(rootId, id))),
      ])
      const inherited: InheritedItemRow[] = inheritedLists.flatMap((list, i) =>
        list.map((item) => ({
          item,
          sourcePath: ancestorPaths[i] ?? '',
        })),
      )

      return {
        tree,
        rootId,
        environments,
        tenantPubkey: tenantKey.tenantPubkey,
        currentKekVersion: tenantKey.currentKekVersion,
        nodeId,
        // Both for the "Open in Projects" jump: Projects addresses a
        // record as `?sel=<id>&selKind=<kind>`, and the descendant count
        // is what tells an operator whether that lands on a leaf they can
        // edit or a branch they will have to walk. Derived from the tree
        // already in hand, so the link costs no request and pulls no
        // Projects module into the vault page.
        nodeKind: found?.node.kind ?? null,
        nodePath,
        nodeDescendants: found ? countDescendants(found.node) : 0,
        items,
        inherited,
      }
    }),
  component: VaultProjectsRoute,
})

function VaultProjectsRoute() {
  const {
    tree,
    rootId,
    environments,
    tenantPubkey,
    currentKekVersion,
    nodeId,
    nodeKind,
    nodePath,
    nodeDescendants,
    items,
    inherited,
  } = Route.useLoaderData()
  const router = useRouter()
  const [editing, setEditing] = useState<VaultItemRow | null | undefined>(
    undefined,
  )
  const [error, setError] = useState<string | null>(null)

  async function refresh() {
    await router.invalidate()
    setEditing(undefined)
  }

  async function handleDelete(item: VaultItemRow) {
    if (!nodeId) return
    if (!window.confirm(`Delete item "${item.key}"? This cannot be undone.`))
      return
    setError(null)
    try {
      await deleteItem(rootId, nodeId, item.key)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'delete failed')
    }
  }

  return (
    <>
      {/* This page is the one vault screen that never had a heading of its
          own — it borrowed the layout route's "Vault" `<h1>`, which is
          gone now that the app shell owns the vault sub-nav. Without this
          the page would have no `<h1>` at all, which is as broken for
          heading navigation as the two it used to render. */}
      <PageHeader
        title="Projects"
        description="Vault nodes and the items stored on them."
      />
      <div className="flex gap-8">
        <VaultNodeTree
          roots={tree.roots}
          selectedNodeId={nodeId}
          onSelect={(id) => {
            setEditing(undefined)
            void router.navigate({
              to: '/vault/projects',
              search: { node: id },
            })
          }}
        />
        <div className="min-w-0 flex-1">
          {nodeId ? (
            <>
              <Toolbar
                label="Selected node"
                actions={
                  <Button
                    variant="primary"
                    onClick={() => {
                      setEditing(null)
                    }}
                  >
                    New item
                  </Button>
                }
              >
                {/* The node path is the string an operator retypes as
                    `stoaria vault run -p …`, so it is monospace and
                    copyable — CLAUDE.md records an hour lost to passing
                    the wrong one (2026-10-03). */}
                <Code isCopyable copyLabel="Copy node path">
                  {nodePath}
                </Code>
              </Toolbar>
              {nodeKind !== null && (
                <OpenInProjectsLink
                  nodeId={nodeId}
                  nodeKind={nodeKind}
                  descendantCount={nodeDescendants}
                />
              )}
              {error !== null && (
                <div className="mb-2">
                  <Callout tone="danger">{error}</Callout>
                </div>
              )}
              <VaultItemsTable
                items={items}
                caption={`Items on ${nodePath}`}
                onEdit={(item) => {
                  setEditing(item)
                }}
                onDelete={(item) => {
                  void handleDelete(item)
                }}
              />
              {inherited.length > 0 && (
                <div className="mt-6">
                  <h3 className="mb-1 text-xs font-medium tracking-wide text-muted uppercase">
                    Inherited
                  </h3>
                  {(() => {
                    const bySource = new Map<string, VaultItemRow[]>()
                    for (const { item, sourcePath } of inherited) {
                      const list = bySource.get(sourcePath) ?? []
                      list.push(item)
                      bySource.set(sourcePath, list)
                    }
                    return [...bySource.entries()].map(([sourcePath, rows]) => (
                      <div key={sourcePath} className="mb-3">
                        <p className="mb-1 text-xs text-faint">
                          from <Code>{sourcePath}</Code>
                        </p>
                        {/* No edit/delete: an inherited item is owned by
                            the ancestor node, and the table drops the
                            column rather than offering controls that
                            would act on the wrong node. */}
                        <VaultItemsTable
                          items={rows}
                          caption={`Items inherited from ${sourcePath}`}
                        />
                      </div>
                    ))
                  })()}
                </div>
              )}
              {editing !== undefined && nodeId && (
                <div className="mt-6 border-t border-subtle pt-4">
                  <VaultItemEditor
                    rootId={rootId}
                    nodeId={nodeId}
                    environments={environments}
                    tenantPubkey={tenantPubkey}
                    currentKekVersion={currentKekVersion}
                    item={editing}
                    onSaved={refresh}
                    onCancel={() => {
                      setEditing(undefined)
                    }}
                  />
                </div>
              )}
            </>
          ) : (
            // Reached only when the root itself is missing — the loader
            // defaults the selection to the first root — so the useful
            // next step is building the org tree, not picking from it.
            <EmptyState
              headline="No node selected"
              description="Vault items hang off an org node. Pick one on the left, or create an entity first."
              action={
                <Button
                  onClick={() => {
                    void router.navigate({ to: '/projects' })
                  }}
                >
                  Open Projects
                </Button>
              }
            />
          )}
        </div>
      </div>
    </>
  )
}
